/**
 * Voucher Code File Upload Component
 * Handles file upload with validation for voucher codes (CSV/XLSX)
 */

import { useState, useCallback } from "react";
import { FileUpload } from "@/components/ui/file-upload";
import type { VoucherCodesValidationResult } from "../../../../domain/model/RewardCatalogModel";
import {
  DownloadVoucherTemplateUseCase,
  ValidateVoucherCodesFileUseCase,
} from "../../../../domain/usecase";

export interface VoucherCodeFileUploadProps {
  readonly id: string;
  readonly value: string;
  readonly onChange: (fileName: string, file?: File | null) => void;
  readonly onValidationComplete?: (result: VoucherCodesValidationResult | null) => void;
  readonly errorMessage?: string;
}

export const VoucherCodeFileUpload = ({
  id,
  value,
  onChange,
  onValidationComplete,
  errorMessage,
}: VoucherCodeFileUploadProps): React.JSX.Element => {
  const [isValidating, setIsValidating] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [validationResult, setValidationResult] = useState<VoucherCodesValidationResult | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleFileChange = useCallback(
    async (fileName: string, file?: File | null) => {
      // Reset validation state
      setValidationResult(null);
      setValidationError(null);

      // Pass the file to parent
      onChange(fileName, file);

      // If no file, skip validation
      if (!file) {
        onValidationComplete?.(null);
        return;
      }

      // Validate file type
      const validExtensions = [".csv", ".xlsx", ".xls"];
      const fileExtension = fileName.toLowerCase().substring(fileName.lastIndexOf("."));
      if (!validExtensions.includes(fileExtension)) {
        setValidationError("Invalid file type. Please upload a CSV or XLSX file.");
        return;
      }

      // Start validation
      setIsValidating(true);
      try {
        const result = await ValidateVoucherCodesFileUseCase(file);
        setValidationResult(result);
        onValidationComplete?.(result);
      } catch (error) {
        console.error("Failed to validate voucher codes file:", error);
        setValidationError("Failed to validate file. Please try again.");
        onValidationComplete?.(null);
      } finally {
        setIsValidating(false);
      }
    },
    [onChange, onValidationComplete]
  );

  const displayError = errorMessage ?? validationError;

  const handleDownloadTemplate = useCallback(async () => {
    setValidationError(null);
    setIsDownloadingTemplate(true);
    try {
      await DownloadVoucherTemplateUseCase();
    } catch (error) {
      console.error("Failed to download voucher template:", error);
      setValidationError("Failed to download template. Please try again.");
    } finally {
      setIsDownloadingTemplate(false);
    }
  }, []);

  return (
    <div className="flex flex-col gap-2">
      <FileUpload
        id={id}
        value={value}
        onChange={handleFileChange}
        accept=".csv,.xlsx,.xls"
        acceptText="File type: .csv, .xlsx"
        maxSizeText="Maximum size: 5MB"
        variant="document"
        showIcon={false}
        templateLink={{
          label: isDownloadingTemplate ? "Downloading..." : "rewards_template.csv",
          onClick: () => {
            if (!isDownloadingTemplate) {
              void handleDownloadTemplate();
            }
          },
        }}
        errorMessage={displayError ?? undefined}
      />

      {isValidating && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
          <span>Validating voucher codes...</span>
        </div>
      )}

      {validationResult && !isValidating && (
        <ValidationResultDisplay result={validationResult} />
      )}
    </div>
  );
};

interface ValidationResultDisplayProps {
  readonly result: VoucherCodesValidationResult;
}

const ValidationResultDisplay = ({ result }: ValidationResultDisplayProps): React.JSX.Element => {
  const isSuccess = result.isValid && result.invalidCodes === 0;
  const hasWarnings = result.warnings.length > 0;

  return (
    <div
      className={`p-3 rounded-lg border ${
        isSuccess
          ? "bg-green-50 border-green-200"
          : hasWarnings
          ? "bg-yellow-50 border-yellow-200"
          : "bg-red-50 border-red-200"
      }`}
    >
      <div className="flex flex-col gap-2">
        {/* Summary */}
        <div className="flex items-center gap-4 text-sm">
          <span className="font-medium">
            {isSuccess ? "✓ Validation Passed" : hasWarnings ? "⚠ Validation Passed with Warnings" : "✗ Validation Failed"}
          </span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="flex flex-col">
            <span className="text-gray-500">Total Codes</span>
            <span className="font-semibold">{result.totalCodes.toLocaleString()}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-gray-500">Valid</span>
            <span className="font-semibold text-green-600">{result.validCodes.toLocaleString()}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-gray-500">Invalid</span>
            <span className={`font-semibold ${result.invalidCodes > 0 ? "text-red-600" : "text-gray-600"}`}>
              {result.invalidCodes.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Warnings */}
        {result.warnings.length > 0 && (
          <div className="text-xs text-yellow-700">
            {result.warnings.map((warning, index) => (
              <div key={index}>⚠ {warning}</div>
            ))}
          </div>
        )}

        {/* Errors */}
        {result.errors.length > 0 && (
          <div className="text-xs text-red-700">
            {result.errors.map((error, index) => (
              <div key={index}>✗ {error}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

